/**
 * fieldInsightStore.js
 *
 * Module-level (outside-React) store owning the "explain this field" chat that
 * pops open when a user clicks/double-clicks a data field in a report window or
 * the portfolio detail tab. Modeled on chatRunStore.js, but:
 *   - state is in-memory ONLY (no sessionStorage, no aiAnalystSession) - a
 *     session is erased when its report/portfolio surface unmounts,
 *   - one chat session per sessionKey (win.id / 'portfolio:'+id),
 *   - each run holds an AbortController so a superseding startExplain (or a
 *     clearSession) aborts the in-flight stream immediately, and
 *   - messages typed while a run is streaming are QUEUED and dispatched in order
 *     once the active stream ends.
 *
 * The snapshot handed to subscribers is { messages, running, queuedCount }.
 */

import { API_ENDPOINTS } from "../config/api";

/**
 * sessionKey -> {
 *   messages, running, streamingId, subscribers:Set, field, draft,
 *   queue:string[], abort:AbortController|null
 * }
 */
const sessions = new Map();

// Namespaced, collision-free message ids (Date.now() collides with chatRunStore).
let idCounter = 0;
const nextId = () => `fi_${Date.now()}_${idCounter++}`;

const ensureSession = (sessionKey) => {
  let s = sessions.get(sessionKey);
  if (!s) {
    s = {
      messages: [],
      running: false,
      streamingId: null,
      subscribers: new Set(),
      field: null,
      draft: "",
      queue: [],
      abort: null,
    };
    sessions.set(sessionKey, s);
  }
  return s;
};

const snapshotOf = (s) => ({
  messages: s.messages,
  running: s.running,
  queuedCount: s.queue.length,
});

const notify = (sessionKey) => {
  const s = sessions.get(sessionKey);
  if (!s) return;
  const snap = snapshotOf(s);
  s.subscribers.forEach((cb) => {
    try {
      cb(snap);
    } catch (_) {
      // a dead subscriber must never break the run
    }
  });
};

// ── message mutations ────────────────────────────────────────────────────────

const upsertStatus = (s, payload) => {
  if (!payload?.message) return;
  const last = s.messages[s.messages.length - 1];
  const entry = {
    id: last && last.role === "status" ? last.id : nextId(),
    role: "status",
    content: payload.message,
    timestamp: new Date(),
  };
  if (last && last.role === "status") {
    s.messages = [...s.messages.slice(0, -1), entry];
  } else {
    s.messages = [...s.messages, entry];
  }
};

const startStreamingMessage = (s) => {
  const sid = nextId();
  s.streamingId = sid;
  const msg = {
    id: sid,
    role: "assistant",
    content: "",
    isStreaming: true,
    timestamp: new Date(),
  };
  const last = s.messages[s.messages.length - 1];
  if (last && last.role === "status") {
    // Replace the trailing status placeholder with the streaming answer.
    s.messages = [...s.messages.slice(0, -1), msg];
  } else {
    s.messages = [...s.messages, msg];
  }
  return sid;
};

const appendToken = (s, text) => {
  const sid = s.streamingId;
  if (!sid) return;
  const idx = s.messages.findIndex((m) => m.id === sid);
  if (idx === -1) return;
  const updated = [...s.messages];
  updated[idx] = { ...updated[idx], content: (updated[idx].content || "") + text };
  s.messages = updated;
};

const finalizeStreamingMessage = (s, data) => {
  const sid = s.streamingId;
  if (sid) {
    const idx = s.messages.findIndex((m) => m.id === sid);
    if (idx !== -1) {
      const updated = [...s.messages];
      const existing = updated[idx];
      updated[idx] = {
        ...existing,
        content: data.markdown || data.response || existing.content,
        isStreaming: false,
      };
      s.messages = updated;
    }
    s.streamingId = null;
    return;
  }
  // No streamed tokens arrived (e.g. final-only response): append fresh.
  s.messages = [
    ...s.messages.filter((m) => m.role !== "status"),
    {
      id: nextId(),
      role: "assistant",
      content: data.markdown || data.response || "",
      isStreaming: false,
      timestamp: new Date(),
    },
  ];
};

const appendErrorAssistant = (s, message) => {
  // Drop a trailing status placeholder; surface the error in its place.
  const last = s.messages[s.messages.length - 1];
  const base = last && last.role === "status" ? s.messages.slice(0, -1) : s.messages;
  s.messages = [
    ...base,
    {
      id: nextId(),
      role: "assistant",
      content: message,
      isStreaming: false,
      timestamp: new Date(),
    },
  ];
  s.streamingId = null;
};

// ── history for the backend ──────────────────────────────────────────────────

/**
 * Prior FINALIZED user/assistant turns, oldest first, capped at the last 20.
 * Excludes status messages and any in-flight (still streaming) assistant turn.
 *
 * `currentId` is the id of the user message this run is answering. That turn and
 * every still-queued user turn are already sitting in s.messages (appended eagerly
 * at enqueue time); they are excluded by id so neither the current message nor a
 * question queued behind it leaks into its own context. Prior answers stay in even
 * though a reply lands positionally after the eagerly-appended queued turns.
 */
const buildHistory = (s, currentId) => {
  const excluded = new Set(s.queue.map((q) => q.id));
  if (currentId) excluded.add(currentId);
  return s.messages
    .filter((m) => (m.role === "user" || m.role === "assistant") && !m.isStreaming)
    .filter((m) => !excluded.has(m.id))
    .map((m) => ({ role: m.role, content: m.content }))
    .slice(-20);
};

// ── the run (SSE reader loop, mirrors chatRunStore.startRun mechanics) ────────

const runInsight = async (
  sessionKey,
  { fetchWithAuth, recordActivity, mode, message, currentUserId }
) => {
  const s = ensureSession(sessionKey);
  const controller = new AbortController();
  s.abort = controller;
  s.running = true;
  s.streamingId = null;

  // History = finalized turns before the user turn this run answers.
  // currentUserId is that turn's id - a typed chat message, or the synthetic
  // "Explain <field>" turn a double-click records. Its message is already in
  // s.messages (possibly followed by still-queued user turns); cut strictly
  // before it so neither the current message nor queued-behind messages leak
  // into their own context.
  const history = buildHistory(s, currentUserId || null);

  const body = {
    mode,
    message: message || "",
    field: s.field || {},
    history,
  };

  notify(sessionKey);

  try {
    const response = await fetchWithAuth(API_ENDPOINTS.fieldInsightStream, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("text/event-stream")) {
      const data = await response.json();
      finalizeStreamingMessage(s, data);
      notify(sessionKey);
      recordActivity?.();
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";

      for (const part of parts) {
        const lines = part.split("\n").filter(Boolean);
        let eventType = "message";
        const dataLines = [];
        for (const line of lines) {
          if (line.startsWith("event:")) {
            eventType = line.replace("event:", "").trim();
          } else if (line.startsWith("data:")) {
            dataLines.push(line.replace("data:", "").trim());
          }
        }
        if (!dataLines.length) continue;
        let payload;
        try {
          payload = JSON.parse(dataLines.join("\n"));
        } catch (_) {
          continue;
        }

        if (eventType === "token" && payload.type === "token") {
          if (!s.streamingId) startStreamingMessage(s);
          appendToken(s, payload.text || "");
          notify(sessionKey);
        } else if (eventType === "status" && payload.type === "status") {
          upsertStatus(s, payload);
          notify(sessionKey);
        } else if (eventType === "stream_end" && payload.type === "stream_end") {
          // metadata arrives with the final event
        } else if (eventType === "final" && payload.type === "final") {
          const data = payload.payload || {};
          finalizeStreamingMessage(s, data);
          notify(sessionKey);
          recordActivity?.();
        } else if (eventType === "error") {
          appendErrorAssistant(s, "Sorry, I encountered an error. Please try again.");
          notify(sessionKey);
        }
      }
    }
  } catch (error) {
    if (error?.name === "AbortError") {
      // Superseded/cleared: leave state as the caller has already reset it.
      return;
    }
    appendErrorAssistant(s, "Sorry, I encountered an error. Please try again.");
    notify(sessionKey);
  } finally {
    if (s.abort === controller) {
      s.running = false;
      s.streamingId = null;
      s.abort = null;
      notify(sessionKey);
      drainQueue(sessionKey, { fetchWithAuth, recordActivity });
    }
  }
};

/**
 * After a run ends, dispatch the oldest queued user message as a chat run.
 * The user turns are already in s.messages (appended at enqueue time); here we
 * just fire the backend call for the head of the queue.
 */
const drainQueue = (sessionKey, { fetchWithAuth, recordActivity }) => {
  const s = sessions.get(sessionKey);
  if (!s || s.running || !s.queue.length) return;
  const { id, text } = s.queue.shift();
  notify(sessionKey);
  // Fire-and-forget; its own finally will drain the next queued item.
  runInsight(sessionKey, {
    fetchWithAuth,
    recordActivity,
    mode: "chat",
    message: text,
    currentUserId: id,
  });
};

// ── public API ───────────────────────────────────────────────────────────────

/**
 * Subscribe to a session. The callback fires immediately with the current
 * snapshot ({ messages, running, queuedCount }) and on every subsequent change.
 * Returns an unsubscribe fn; unsubscribing does NOT stop an in-flight run.
 */
export const subscribe = (sessionKey, cb) => {
  const s = ensureSession(sessionKey);
  s.subscribers.add(cb);
  cb(snapshotOf(s));
  return () => {
    s.subscribers.delete(cb);
  };
};

/** Remember the last-clicked FieldContext for this session. */
export const setField = (sessionKey, field) => {
  const s = ensureSession(sessionKey);
  s.field = field || null;
};

/** Is a run currently in flight for this session? */
export const isRunning = (sessionKey) => !!sessions.get(sessionKey)?.running;

export const getDraft = (sessionKey) => sessions.get(sessionKey)?.draft || "";

export const setDraft = (sessionKey, text) => {
  const s = ensureSession(sessionKey);
  s.draft = text || "";
};

/**
 * The transcript entry recorded for a double-clicked field, so consecutive
 * explanations read as distinct question/answer turns.
 */
const explainLabelFor = (field) => {
  const f = field || {};
  const name = f.column_label || f.field_label || "this field";
  const value = f.field_value ? ` = ${f.field_value}` : "";
  const desc = String(f.row_description || "");
  const row = desc ? ` (${desc.slice(0, 60)}${desc.length > 60 ? "..." : ""})` : "";
  return `Explain ${name}${value}${row}`;
};

/**
 * Set the field + abort any active run in this session + start a fresh
 * mode:'explain' run for the clicked field. A double-click while streaming lands
 * here: the in-flight run is aborted and the new explanation starts immediately.
 * The click is recorded as a user turn ("Explain <field> = <value> (<row>)") so
 * the transcript shows what each explanation answers.
 */
export const startExplain = (sessionKey, { fetchWithAuth, recordActivity, field }) => {
  const s = ensureSession(sessionKey);
  s.field = field || null;
  // Abort any in-flight run and clear its queue - a new explain supersedes.
  if (s.abort) {
    const prev = s.abort;
    s.abort = null;
    prev.abort();
  }
  s.running = false;
  // The superseded run's partial answer stops streaming and stays as a finalized
  // partial (an empty one is dropped) - never a forever-pulsing caret.
  if (s.streamingId) {
    const idx = s.messages.findIndex((m) => m.id === s.streamingId);
    if (idx !== -1) {
      if (s.messages[idx].content) {
        const updated = [...s.messages];
        updated[idx] = { ...updated[idx], isStreaming: false };
        s.messages = updated;
      } else {
        s.messages = s.messages.filter((m) => m.id !== s.streamingId);
      }
    }
    s.streamingId = null;
  }
  // Drop still-queued user turns: their dispatch is being superseded, so strip
  // them from s.messages too. Otherwise the abandoned question lingers as an
  // unanswered bubble AND leaks into this fresh explain run's history context.
  if (s.queue.length) {
    const queuedIds = new Set(s.queue.map((q) => q.id));
    s.messages = s.messages.filter((m) => !queuedIds.has(m.id));
  }
  s.queue = [];
  const userId = nextId();
  s.messages = [
    ...s.messages,
    { id: userId, role: "user", content: explainLabelFor(s.field), timestamp: new Date() },
  ];
  runInsight(sessionKey, {
    fetchWithAuth,
    recordActivity,
    mode: "explain",
    message: "",
    currentUserId: userId,
  });
};

/**
 * Append a user message. If a run is in flight, queue it (dispatched in order
 * when the active stream ends); otherwise start a mode:'chat' run immediately
 * using the last-clicked field.
 */
export const sendMessage = (sessionKey, { fetchWithAuth, recordActivity, text }) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return;
  const s = ensureSession(sessionKey);
  const userId = nextId();
  s.messages = [
    ...s.messages,
    { id: userId, role: "user", content: trimmed, timestamp: new Date() },
  ];
  if (s.running) {
    s.queue = [...s.queue, { id: userId, text: trimmed }];
    notify(sessionKey);
    return;
  }
  notify(sessionKey);
  runInsight(sessionKey, {
    fetchWithAuth,
    recordActivity,
    mode: "chat",
    message: trimmed,
    currentUserId: userId,
  });
};

/** Abort the active run and drop messages + draft + queue + field. */
export const clearSession = (sessionKey) => {
  const s = sessions.get(sessionKey);
  if (!s) return;
  if (s.abort) {
    const prev = s.abort;
    s.abort = null;
    prev.abort();
  }
  s.messages = [];
  s.draft = "";
  s.queue = [];
  s.field = null;
  s.running = false;
  s.streamingId = null;
  notify(sessionKey);
};
